import {
  instanceOfNumericEvaluationParameterDto,
  type DiscreteNumericValueDto,
  type EvaluationParameterDto,
  type NumericValueDto,
  type QuantityValueType,
} from '@/api/generator';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { isNumber } from 'lodash';
import { useAccountStore } from '@/stores/accountStore';

export interface NumberDisplayOptions {
  valueType?: QuantityValueType | null;
  fractionDigits?: number | null;
  scientific?: boolean;
  signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative';
}

interface NumberDisplayOptionsWithUnit extends NumberDisplayOptions {
  unitAbbreviation?: string | null;
}

/**
 * Formats a numeric value for display, optionally with a unit abbreviation.
 * @param value The numeric value to be formatted.
 * @param options Options for formatting the value, including the number of fraction digits and the unit abbreviation.
 * @returns A formatted string representing the numeric value, with the unit abbreviation if provided.
 */
export const toDisplayValue = (value?: number | null, options?: NumberDisplayOptionsWithUnit) => {
  const formattedNumber = formatNumber(value, options);
  if (formattedNumber.length) {
    return [formattedNumber, options?.unitAbbreviation].filter(Boolean).join('\u00A0');
  } else {
    return EMPTY_FIELD_SIGN;
  }
};

/**
 * Parenthesized range. Decimal length follows the number itself, not displayed-decimals.
 * Unit, when present, sits once after the closing parenthesis.
 */
export const toRangeDisplay = (min?: number | null, max?: number | null, abbreviation?: string) => {
  if (!isValidNumber(min) && !isValidNumber(max)) return EMPTY_FIELD_SIGN;

  if (!isValidNumber(max)) {
    const sign = min! > 0 ? '+' : '';
    return `\u2265\u00A0${sign}${toDisplayValue(min, {
      fractionDigits: decimalPlacesInNumber(min!),
      unitAbbreviation: abbreviation,
    })}`;
  }

  if (!isValidNumber(min)) {
    const sign = max > 0 ? '+' : '';
    return `\u2264\u00A0${sign}${toDisplayValue(max, {
      fractionDigits: decimalPlacesInNumber(max),
      unitAbbreviation: abbreviation,
    })}`;
  }

  const sign = min < 0 && max > 0 ? '+' : '';

  return `(${formatRangeBound(min)} ... ${sign}${formatRangeBound(max)})${
    abbreviation ? `\u00A0${abbreviation}` : ''
  }`;
};

/**
 * Bounds for ValidationValueNotInRange. Same decimal length and sign rule as toRangeDisplay.
 */
export function formatRangeHintBounds({ min, max }: { min?: number | null; max?: number | null }): {
  minValue?: string;
  maxValue?: string;
} {
  const minValue = isValidNumber(min) ? formatRangeBound(min) : undefined;
  let maxValue = isValidNumber(max) ? formatRangeBound(max) : undefined;
  if (isValidNumber(min) && isValidNumber(max) && min < 0 && max > 0 && maxValue) {
    maxValue = `+${maxValue}`;
  }
  return { minValue, maxValue };
}

function formatRangeBound(value: number): string {
  return formatNumber(value, { fractionDigits: decimalPlacesInNumber(value) });
}

// ponytail: length comes from Number.toString(), so a typed trailing zero (5.100) collapses to 5.1.
// Upgrade path: keep the raw input string on the value.
function decimalPlacesInNumber(value: number): number {
  const [coeff, exp] = value.toString().toLowerCase().split('e');
  const fractionLength = coeff.split('.')[1]?.length ?? 0;
  const exponent = exp ? Number(exp) : 0;
  return Math.max(0, fractionLength - exponent);
}

export const toRounded = (value: number, fractionDigits?: number | null) => {
  if (!isNumber(value) || !isNumber(fractionDigits)) {
    return value;
  }

  const roundFactor = Math.pow(10, fractionDigits);
  return Math.round(value * roundFactor) / roundFactor;
};

export function getFormattingOptionsFromNumericValue(
  numericValue: NumericValueDto | DiscreteNumericValueDto,
): NumberDisplayOptionsWithUnit {
  return {
    fractionDigits: numericValue.decimalPlaces,
    unitAbbreviation: numericValue.unitAbbreviation,
    valueType: numericValue.valueType,
  };
}

export function getFormattingOptionsFromEvaluationParameter(
  evaluation: EvaluationParameterDto,
): NumberDisplayOptionsWithUnit | undefined {
  if (instanceOfNumericEvaluationParameterDto(evaluation)) {
    const valueTypeFromDefinition = evaluation.definition?.valueType;
    const evaluationType = evaluation.evaluationType;

    const isIntegerEvaluation =
      valueTypeFromDefinition === 'IntegerNumber' &&
      evaluationType !== 'Ratio' &&
      evaluationType !== 'RelativeDifference';

    const computedValueType: QuantityValueType | null = isIntegerEvaluation
      ? 'IntegerNumber'
      : 'FloatingPoint';

    let fractionDigits: number | null | undefined;
    if (isIntegerEvaluation) {
      fractionDigits = 0;
    } else if (valueTypeFromDefinition === 'IntegerNumber') {
      // Ratio and RelativeDifference for integer value types should default to 3
      fractionDigits = 3;
    } else {
      // Use provided decimalPlaces for floating types; fallback to 3 when missing
      fractionDigits = evaluation.decimalPlaces ?? 3;
    }

    const unitAbbreviation = evaluation.evaluationUnitAbbreviation;

    return {
      valueType: computedValueType,
      fractionDigits,
      unitAbbreviation,
      // Ensure consistent sign behavior for display of limits
      signDisplay: 'exceptZero',
    };
  }
}

/**
 * Precondition: number is provided optimized from parseNumberIntl
 * This function is used in NumberField component only.
 */
export const formatNumber = (value: number | undefined | null, format?: NumberDisplayOptions) => {
  if (!isValidNumber(value)) {
    return '';
  }

  let targetNumber = value;
  let fractionDigits = format?.fractionDigits ?? undefined;

  if (format?.valueType === 'IntegerNumber') {
    targetNumber = toRounded(value, 0);
    fractionDigits = fractionDigits ?? 0;
  } else if (isValidNumber(format?.fractionDigits)) {
    targetNumber = toRounded(value, format.fractionDigits);
  }

  const language = useAccountStore().language;

  if (format?.scientific) {
    const exp = Number(targetNumber).toExponential(format?.fractionDigits ?? undefined);
    const [coeff, expPart] = exp.split('e');
    const localizedCoeff = new Intl.NumberFormat(language).format(Number(coeff));
    return `${localizedCoeff}e${expPart}`;
  } else {
    return new Intl.NumberFormat(language, {
      maximumFractionDigits: 100,
      minimumFractionDigits: fractionDigits,
      signDisplay: format?.signDisplay ?? 'negative',
      useGrouping: false,
    }).format(targetNumber);
  }
};

/**
 * Parses a number and makes sure, that the parsed result has no higher accuracy than the target format
 * https://www.regular-expressions.info/floatingpoint.html
 */
export const parseNumber = (value: string) => {
  // return undefined for empty strings
  let numberString = value.trim();
  if (!numberString) {
    return undefined;
  }

  if (getDecimalSeparator(useAccountStore().language) === ',') {
    if (numberString.includes('.')) {
      return NaN;
    }
    numberString = numberString.replace(',', '.');
  }

  // return NaN for invalid strings
  // Use a regex that avoids catastrophic backtracking by separating cases explicitly
  // Accepts: integers (123), decimals with leading digits (123.45), and decimals without leading digits (.45)
  // Optional scientific exponent part at the end
  const allowedChars = /^[-+]?(?:\d+\.\d+|\d+|\.\d+)(?:[eE][-+]?\d+)?$/;
  const numberValue = parseFloat(numberString);
  if (!allowedChars.test(numberString) || isNaN(numberValue)) {
    return NaN;
  }

  return numberValue;
};

export function getDecimalSeparator(locale: string) {
  const numberWithDecimal = 1.1;
  const formatted = new Intl.NumberFormat(locale).format(numberWithDecimal);
  return formatted.replace(/\d/g, '')[0];
}

function isValidNumber(value: unknown): value is number {
  return isNumber(value) && !isNaN(value);
}
